import { useCallback, useMemo, useState, type ChangeEvent } from 'react';
import { useGetList, useNotify, useStore } from 'react-admin';
import {
    Box,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    AnimationId,
    AntennaId,
    AntennaLight,
    AnimationLight,
    MissionTypeId,
} from '@wwf/sdk';
import { getAnimationInventory, updateAnimationInventory } from '@wwf/sdk';
import { InventoryByAntennaFilterToolbar } from '@app/inventory/InventoryByAntennaFilterToolbar';
import { useMissionTypes } from '@app/mission_type/useMissionType';
import {
    AnimationInventoryEditCellDialog,
    type AnimationInventorySaveValues,
    type EditCellState,
} from './AnimationInventoryEditCellDialog';
import {
    applyAnimationInventoryFilters,
    EMPTY_ANIMATION_INVENTORY_FILTERS,
    hasActiveAnimationInventoryFilters,
    type AnimationInventoryFilters,
} from './animationInventoryFilters';
import { AntennaLightCard } from '@app/antenna/show/AntennaLightCard';
import { AnimationLightCard } from '../show/AnimationLightCard';

const INVENTORY_QUERY_KEY = ['animation_inventory_per_antenna'] as const;
const COMPRESSED_VIEW_STORE_KEY = 'animation.inventory.compressedView';
const FILTERS_STORE_KEY = 'animation.inventory.filters';
const COMPRESSED_ANIMATION_COLUMN_WIDTH = 180;
const COMPRESSED_ANTENNA_COLUMN_WIDTH = 88;

function inventoryCellKey(antennaId: AntennaId, animationId: AnimationId) {
    return `${antennaId}\0${animationId}`;
}

export const AnimationInventoryByAntenna = () => {
    const notify = useNotify();
    const queryClient = useQueryClient();
    const { data: missionTypes = [] } = useMissionTypes();

    const { data: antennas, isPending: antennasPending } =
        useGetList<AntennaLight>('antenna', {
            pagination: { page: 0, perPage: 10000 },
            sort: { field: 'name', order: 'ASC' },
        });

    const { data: animations, isPending: animationsPending } =
        useGetList<AnimationLight>('animation', {
            pagination: { page: 0, perPage: 10000 },
            sort: { field: 'name', order: 'ASC' },
            filter: { enabled: true },
        });

    const {
        data: inventoryRows,
        isPending: inventoryPending,
        isError: inventoryError,
    } = useQuery({
        queryKey: INVENTORY_QUERY_KEY,
        queryFn: () => getAnimationInventory(),
    });

    const cellByKey = useMemo(() => {
        const m = new Map<
            string,
            { count: number; inventoryComment: string | null }
        >();
        for (const row of inventoryRows ?? []) {
            m.set(inventoryCellKey(row.antennaId, row.animationId), {
                count: row.inventoryCount ?? 0,
                inventoryComment: row.inventoryComment,
            });
        }
        return m;
    }, [inventoryRows]);

    const upsertMutation = useMutation({
        mutationFn: async (payload: {
            antennaId: AntennaId;
            animation_id: AnimationId;
            count: number;
            inventoryComment: string | null;
        }) => {
            await updateAnimationInventory(
                payload.animation_id,
                payload.antennaId,
                {
                    inventoryCount: payload.count,
                    inventoryComment: payload.inventoryComment,
                }
            );
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: INVENTORY_QUERY_KEY,
            });
            notify('Inventory updated', { type: 'success' });
        },
        onError: (err: Error) => {
            notify(err.message ?? 'Update failed', { type: 'error' });
        },
    });

    const [editCell, setEditCell] = useState<EditCellState | null>(null);
    const [compressedView, setCompressedView] = useStore<boolean>(
        COMPRESSED_VIEW_STORE_KEY,
        false
    );
    const [filters, setFilters] = useStore<AnimationInventoryFilters>(
        FILTERS_STORE_KEY,
        EMPTY_ANIMATION_INVENTORY_FILTERS
    );

    const getInventoryCount = useCallback(
        (animationId: AnimationId, antennaId: AntennaId) =>
            cellByKey.get(inventoryCellKey(antennaId, animationId))?.count ?? 0,
        [cellByKey]
    );

    const handleCompressedViewChange = useCallback(
        (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
            setCompressedView(checked);
        },
        [setCompressedView]
    );

    const openEdit = useCallback((params: EditCellState) => {
        setEditCell({
            antennaId: params.antennaId,
            animationId: params.animationId,
            antennaName: params.antennaName,
            animationName: params.animationName,
            count: params.count,
            inventoryComment: params.inventoryComment,
            noSpecificMaterialRequired: params.noSpecificMaterialRequired,
        });
    }, []);

    const closeEdit = useCallback(() => {
        setEditCell(null);
    }, []);

    const handleSaveEdit = useCallback(
        (values: AnimationInventorySaveValues) => {
            if (!editCell) return;
            upsertMutation.mutate(
                {
                    antennaId: editCell.antennaId,
                    animation_id: editCell.animationId,
                    count: values.count,
                    inventoryComment: values.inventoryComment,
                },
                { onSuccess: closeEdit }
            );
        },
        [closeEdit, editCell, upsertMutation]
    );

    const { animations: filteredAnimationList, antennas: filteredAntennaList } =
        useMemo(
            () =>
                applyAnimationInventoryFilters({
                    animations: animations ?? [],
                    antennas: antennas ?? [],
                    filters,
                    getInventoryCount,
                }),
            [animations, antennas, filters, getInventoryCount]
        );

    const handleClearFilters = useCallback(() => {
        setFilters(EMPTY_ANIMATION_INVENTORY_FILTERS);
    }, [setFilters]);

    if (antennasPending || animationsPending || inventoryPending) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (inventoryError) {
        return (
            <Typography color="error">
                Could not load inventory. Try again or check your permissions.
            </Typography>
        );
    }

    const antennaList = antennas ?? [];
    const animationList = animations ?? [];

    return (
        <Box sx={{ pt: 2 }}>
            <InventoryByAntennaFilterToolbar
                rowSearchLabel="Animations"
                rowSearchPlaceholder="Search name, description, theme..."
                filters={filters}
                onFiltersChange={baseFilters =>
                    setFilters({ ...filters, ...baseFilters })
                }
                hasActiveFilters={hasActiveAnimationInventoryFilters(filters)}
                onClearFilters={handleClearFilters}
                extraFilters={
                    <>
                        <TextField
                            select
                            size="small"
                            label="Mission type"
                            value={filters.missionType}
                            onChange={event =>
                                setFilters({
                                    ...filters,
                                    missionType: event.target.value as
                                        | MissionTypeId
                                        | '',
                                })
                            }
                            sx={{ minWidth: 180, flex: '0 1 180px' }}
                        >
                            <MenuItem value="">All mission types</MenuItem>
                            {missionTypes.map(missionType => (
                                <MenuItem
                                    key={missionType.id}
                                    value={missionType.id}
                                >
                                    {missionType.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={filters.hideNoMaterialRequired}
                                    onChange={(_event, checked) =>
                                        setFilters({
                                            ...filters,
                                            hideNoMaterialRequired: checked,
                                        })
                                    }
                                />
                            }
                            label="Hide no-material animations"
                            sx={{ ml: 0.5, flexShrink: 0 }}
                        />
                    </>
                }
            />

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                sx={{ mb: 2 }}
            >
                <Typography variant="body2" color="text.secondary">
                    Stock count per animation (rows) and antenna (columns).
                    Click a cell to edit the count and optional comment.
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={compressedView}
                            onChange={handleCompressedViewChange}
                        />
                    }
                    label="Compressed view"
                    sx={{ flexShrink: 0, mt: 1 }}
                />
            </Box>

            {!animationList.length ? (
                <Typography variant="body2" color="text.secondary">
                    No enabled animations to display.
                </Typography>
            ) : !antennaList.length ? (
                <Typography variant="body2" color="text.secondary">
                    No antennas to display.
                </Typography>
            ) : !filteredAnimationList.length || !filteredAntennaList.length ? (
                <Typography variant="body2" color="text.secondary">
                    No animations or antennas match the current filters.
                </Typography>
            ) : (
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                        // Scroll inside the container so `stickyHeader` / `top: 0` header cells stick.
                        maxHeight: '80dvh',
                        width: '80dvw',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={
                            compressedView
                                ? {
                                      tableLayout: 'fixed',
                                      minWidth:
                                          COMPRESSED_ANIMATION_COLUMN_WIDTH +
                                          filteredAntennaList.length *
                                              COMPRESSED_ANTENNA_COLUMN_WIDTH,
                                  }
                                : undefined
                        }
                    >
                        {compressedView && (
                            <colgroup>
                                <col
                                    style={{
                                        width: COMPRESSED_ANIMATION_COLUMN_WIDTH,
                                    }}
                                />
                                {filteredAntennaList.map(antenna => (
                                    <col
                                        key={antenna.id}
                                        style={{
                                            width: COMPRESSED_ANTENNA_COLUMN_WIDTH,
                                        }}
                                    />
                                ))}
                            </colgroup>
                        )}
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        position: 'sticky',
                                        left: 0,
                                        top: 0,
                                        zIndex: 5,
                                        bgcolor: 'background.paper',
                                        minWidth: compressedView
                                            ? COMPRESSED_ANIMATION_COLUMN_WIDTH
                                            : 220,
                                        width: compressedView
                                            ? COMPRESSED_ANIMATION_COLUMN_WIDTH
                                            : undefined,
                                        maxWidth: compressedView
                                            ? COMPRESSED_ANIMATION_COLUMN_WIDTH
                                            : undefined,
                                        boxShadow: theme =>
                                            `1px 0 0 0 ${theme.palette.divider}`,
                                    }}
                                >
                                    Animation
                                </TableCell>
                                {filteredAntennaList.map(antenna => (
                                    <TableCell
                                        key={antenna.id}
                                        align="center"
                                        sx={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 3,
                                            bgcolor: 'background.paper',
                                            minWidth: compressedView
                                                ? COMPRESSED_ANTENNA_COLUMN_WIDTH
                                                : 120,
                                            width: compressedView
                                                ? COMPRESSED_ANTENNA_COLUMN_WIDTH
                                                : undefined,
                                            maxWidth: compressedView
                                                ? COMPRESSED_ANTENNA_COLUMN_WIDTH
                                                : 160,
                                            overflow: 'hidden',
                                            whiteSpace: compressedView
                                                ? 'nowrap'
                                                : 'normal',
                                            verticalAlign: 'bottom',
                                            px: compressedView
                                                ? 0.75
                                                : undefined,
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            justifyContent="flex-start"
                                            width="100%"
                                            overflow="hidden"
                                        >
                                            <AntennaLightCard
                                                record={antenna}
                                            />
                                        </Box>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredAnimationList.map(animation => (
                                <TableRow key={animation.id} hover>
                                    <TableCell
                                        sx={{
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                            bgcolor: 'background.paper',
                                            fontWeight: 500,
                                            width: compressedView
                                                ? COMPRESSED_ANIMATION_COLUMN_WIDTH
                                                : undefined,
                                            maxWidth: compressedView
                                                ? COMPRESSED_ANIMATION_COLUMN_WIDTH
                                                : undefined,
                                            overflow: 'hidden',
                                            boxShadow: theme =>
                                                `1px 0 0 0 ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Box overflow="hidden">
                                            <AnimationLightCard
                                                record={animation}
                                                compressed={compressedView}
                                                forceAvailable
                                            />
                                        </Box>
                                    </TableCell>
                                    {filteredAntennaList.map(antenna => {
                                        const cell = cellByKey.get(
                                            inventoryCellKey(
                                                antenna.id,
                                                animation.id
                                            )
                                        ) ?? {
                                            count: 0,
                                            inventoryComment: null,
                                        };
                                        const { count, inventoryComment } =
                                            cell;
                                        const noSpecificMaterialRequired =
                                            animation.noSpecificMaterialRequired ===
                                            true;
                                        const commentPreview =
                                            inventoryComment &&
                                            inventoryComment.length > 40
                                                ? `${inventoryComment.slice(0, 40)}…`
                                                : inventoryComment;

                                        return (
                                            <TableCell
                                                key={`${animation.id}-${antenna.id}`}
                                                align="center"
                                                onClick={() =>
                                                    openEdit({
                                                        antennaId: antenna.id,
                                                        animationId:
                                                            animation.id,
                                                        antennaName:
                                                            antenna.name,
                                                        animationName:
                                                            animation.name ??
                                                            'Animation',
                                                        count,
                                                        inventoryComment:
                                                            inventoryComment,
                                                        noSpecificMaterialRequired,
                                                    })
                                                }
                                                sx={{
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    verticalAlign: 'middle',
                                                    width: compressedView
                                                        ? COMPRESSED_ANTENNA_COLUMN_WIDTH
                                                        : undefined,
                                                    maxWidth: compressedView
                                                        ? COMPRESSED_ANTENNA_COLUMN_WIDTH
                                                        : 160,
                                                    px: compressedView
                                                        ? 0.75
                                                        : undefined,
                                                    '&:hover': {
                                                        bgcolor: 'action.hover',
                                                    },
                                                }}
                                                title={
                                                    [
                                                        noSpecificMaterialRequired
                                                            ? 'No specific material required — count is not editable.'
                                                            : null,
                                                        inventoryComment,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' ') || undefined
                                                }
                                                aria-label={`Count for ${animation.name} at ${antenna.name}: ${count}${noSpecificMaterialRequired ? '. No specific material required; count is not editable' : ''}${inventoryComment ? `. Comment: ${inventoryComment}` : ''}. Click to edit.`}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: 0.25,
                                                        py: 0.25,
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        {noSpecificMaterialRequired
                                                            ? 'N/A'
                                                            : count}
                                                    </Typography>
                                                    {commentPreview ? (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                lineHeight: 1.2,
                                                                display:
                                                                    '-webkit-box',
                                                                WebkitLineClamp:
                                                                    compressedView
                                                                        ? 1
                                                                        : 2,
                                                                WebkitBoxOrient:
                                                                    'vertical',
                                                                overflow:
                                                                    'hidden',
                                                                wordBreak:
                                                                    'break-word',
                                                            }}
                                                        >
                                                            {commentPreview}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <AnimationInventoryEditCellDialog
                cell={editCell}
                onClose={closeEdit}
                onSave={handleSaveEdit}
                isSavePending={upsertMutation.isPending}
            />
        </Box>
    );
};
